// src/constants/pageSEO.js

import constants from "./constants";
import globalState from "../utils/globalState";

// Cache user parameters for reuse
let cachedUserParams = null;

const getAllUserParams = () => {
  if (!cachedUserParams) {
    cachedUserParams = globalState.getAllUserParams();
  }
  return cachedUserParams;
};

const getCurrentAgentName = () => getAllUserParams().agentName;
const getCurrentAgentTitle = () => getAllUserParams().agentTitle;
const getCurrentAgentNumber = () => getAllUserParams().agentNumber;
const getCurrentAgentEmail = () => getAllUserParams().agentEmail;
const getCurrentBrokerage = () => getAllUserParams().brokerage;
const getCurrentQuote = () => getAllUserParams().quote;
const getCurrentAbout = () => getAllUserParams().about;
const getCurrentHeadshot = () => getAllUserParams().headshot;

const pageSEO = {
  home: () => ({
    title: `${getCurrentAgentName()} - ${getCurrentAgentTitle()} at ${getCurrentBrokerage()}`,
    description: `${getCurrentAbout()} ${getCurrentQuote() || ""}`.trim(),
    keywords: `${getCurrentAgentName()}, ${getCurrentBrokerage()}, real estate, ${getCurrentAgentTitle()}, property expert`,
    path: "/",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name: getCurrentAgentName(),
      image: getCurrentHeadshot(),
      description: getCurrentAbout(),
      telephone: getCurrentAgentNumber(),
      email: getCurrentAgentEmail(),
      worksFor: {
        "@type": "RealEstateOrganization",
        name: getCurrentBrokerage(),
      },
    },
  }),

  calculator: () => ({
    title: `Mortgage Calculator | ${getCurrentAgentName()}`,
    description: `Use our mortgage calculator to estimate payments and affordability with guidance from ${getCurrentAgentName()} at ${getCurrentBrokerage()}.`,
    keywords: `mortgage calculator, loan calculator, monthly payments, interest, home buying budget, ${getCurrentAgentName()}`,
    path: "/calculator",
  }),

  listing: (listing) => ({
    // title: `${listing.address} - ${listing.location} | ${getCurrentAgentName()}`,
    title: `${listing.StreetNumber} ${listing.StreetName} ${listing.StreetSuffix} - ${listing.City} ${listing.StateOrProvince} | ${getCurrentAgentName()}`,
    description: `${listing.bedrooms} bedroom ${listing.bathrooms} bathroom ${listing.propertyType} located in ${listing.location}, priced at ${listing.price}. Contact ${getCurrentAgentName()} for more information.`,
    keywords: `${listing.location}, ${listing.propertyType}, ${listing.bedrooms} bedroom, ${listing.bathrooms} bathroom, property, ${getCurrentAgentName()}`,
    path: `/listing/${listing.id}`,
    image: listing.mainImage,
    type: "product",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      name: listing.address,
      description: listing.description,
      image: listing.mainImage,
      offers: {
        "@type": "Offer",
        priceCurrency: "CAD",
        price: listing?.price?.replace(/[^\d.]/g, ""),
        availability: "https://schema.org/InStock",
      },
    },
  }),

  listings: () => ({
    title: `Property Listings | ${getCurrentAgentName()}`,
    description: `Browse the latest properties with ${getCurrentAgentName()} at ${getCurrentBrokerage()}. Discover condos, single-family homes, and investment opportunities.`,
    keywords: `property listings, real estate, ${getCurrentAgentName()}, ${getCurrentBrokerage()}, homes for sale`,
    path: "/listings",
  }),

  about: () => ({
    title: `About ${getCurrentAgentName()} | ${getCurrentBrokerage()}`,
    description: `Learn about ${getCurrentAgentName()}, ${getCurrentAgentTitle()} at ${getCurrentBrokerage()}. ${getCurrentAbout()}`,
    keywords: `about ${getCurrentAgentName()}, ${getCurrentBrokerage()}, real estate agent, ${getCurrentAgentTitle()}`,
    path: "/about",
  }),

  evaluation: () => ({
    title: `Property Evaluation | ${getCurrentAgentName()}`,
    description: `Request a free market evaluation from ${getCurrentAgentName()} at ${getCurrentBrokerage()} to understand your property's value today.`,
    keywords: `property evaluation, home value, market analysis, real estate assessment, ${getCurrentAgentName()}`,
    path: "/evaluation",
  }),

  reports: () => ({
    title: `Real Estate Market Reports | ${getCurrentAgentName()}`,
    description: `Stay informed with market insights from ${getCurrentAgentName()} at ${getCurrentBrokerage()}. Access price trends and local market analysis.`,
    keywords: `real estate market, market reports, price trends, market analysis, ${getCurrentAgentName()}`,
    path: "/real-estate-reports",
  }),

  contact: () => ({
    title: `Contact ${getCurrentAgentName()} | ${getCurrentBrokerage()}`,
    description: `Contact ${getCurrentAgentName()}, ${getCurrentAgentTitle()} at ${getCurrentBrokerage()} for expert guidance. Phone: ${getCurrentAgentNumber()}, Email: ${getCurrentAgentEmail()}`,
    keywords: `contact ${getCurrentAgentName()}, real estate agent, ${getCurrentBrokerage()}, property consultation`,
    path: "/contact",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "ContactPoint",
      telephone: getCurrentAgentNumber(),
      contactType: "customer service",
      email: getCurrentAgentEmail(),
      availableLanguage: "English",
    },
  }),

  agents: () => ({
    title: "Our Real Estate Agents",
    description:
      "Meet our team of professional real estate agents. Each agent brings extensive experience and expertise to help you buy or sell your property.",
    keywords:
      "real estate agents, realtors, property experts, real estate professionals",
    path: "/agents",
  }),

  commercial: () => ({
    title: `Commercial Real Estate | ${getCurrentAgentName()}`,
    description: `Explore commercial listings with ${getCurrentAgentName()} at ${getCurrentBrokerage()}, including office, retail, and investment properties.`,
    keywords: `commercial real estate, office space, retail space, investment property, ${getCurrentBrokerage()}`,
    path: "/commercial",
  }),

  sellerGuide: () => ({
    title: `Seller's Guide | ${getCurrentAgentName()}`,
    description:
      "Professional guide to selling your home, providing tips on preparation, pricing strategy, and marketing techniques to achieve the best result.",
    keywords:
      "home selling, seller's guide, property sales, real estate market, pricing",
    path: "/selling-guide",
  }),

  buyerGuide: () => ({
    title: `Buyer's Guide | ${getCurrentAgentName()}`,
    description:
      "Comprehensive guide to buying a home, covering financing options, the purchase process, and key factors to consider when investing in property.",
    keywords:
      "home buying, buyer's guide, real estate, buying process, mortgage",
    path: "/buying-guide",
  }),

  location: (location) => ({
    title: `${location.name} Real Estate and Properties`,
    description: `Browse the latest properties, real estate market trends, and community information in ${location.name}. Learn about housing prices, schools, and local amenities in ${location.name}.`,
    keywords: `${location.name}, real estate, properties, homes for sale, ${location.name} community, ${location.name} housing market`,
    path: `/location/${location.slug}`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Place",
      name: location.name,
      description: `Browse the latest properties, real estate market trends, and community information in ${location.name}.`,
    },
  }),
};

globalState.addListener(() => {
  cachedUserParams = null;
});

export default pageSEO;
